import React, { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Loader2, MessageSquare, CheckCircle } from 'lucide-react';

export default function SupportPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    type: 'tool-unavailable',
    toolName: '',
    description: '',
    contact: '',
  });

  const problemTypes = [
    { value: 'tool-unavailable', label: '工具无法打开' },
    { value: 'function-error', label: '功能使用异常' },
    { value: 'content-generation', label: '内容生成问题' },
    { value: 'other', label: '其他问题' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.toolName || !formData.description) {
      toast.error('请填写工具名称和问题描述');
      return;
    }

    setLoading(true);
    
    // 模拟提交
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      toast.success('申诉已提交，我们会尽快处理');
    }, 1500);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <CardHeader>
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-3xl">提交成功！</CardTitle>
              <CardDescription className="text-lg mt-2">
                您的申诉已收到，我们会尽快处理
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-primary/5 rounded-lg p-6 text-left">
                <p className="font-semibold mb-3">📌 处理流程：</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• 客服团队将在24小时内查看您的申诉</li>
                  <li>• 我们会通过您留下的联系方式与您沟通</li>
                  <li>• 问题解决后会及时通知您</li>
                  <li>• 感谢您的反馈，帮助我们改进服务</li>
                </ul>
              </div>

              <Button size="lg" className="w-full" onClick={() => window.location.href = '/'}>
                返回首页
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <Navbar />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-primary to-purple-600 mb-4">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-4">客服申诉</h1>
            <p className="text-lg text-muted-foreground">
              遇到问题？告诉我们，我们会尽快帮您解决
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>问题反馈</CardTitle>
              <CardDescription>
                请详细描述您遇到的问题，我们会尽快为您处理
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-3">
                  <Label>问题类型 <span className="text-red-500">*</span></Label>
                  <RadioGroup
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    {problemTypes.map((type) => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={type.value} id={type.value} />
                        <Label htmlFor={type.value} className="font-normal cursor-pointer">
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="toolName">
                    涉及工具 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="toolName"
                    placeholder="请填写工具名称或链接"
                    value={formData.toolName}
                    onChange={(e) => setFormData({ ...formData, toolName: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    问题描述 <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="请详细描述您遇到的问题，包括操作步骤、错误提示等..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="min-h-[150px]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact">
                    联系方式（选填）
                  </Label>
                  <Input
                    id="contact"
                    placeholder="邮箱或手机号，便于我们快速回复"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  />
                </div>

                <div className="bg-primary/5 rounded-lg p-4 space-y-2">
                  <p className="font-semibold text-sm">💡 温馨提示：</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• 请尽量详细描述问题，有助于我们快速定位</li>
                    <li>• 如有截图或错误信息，可在描述中说明</li>
                    <li>• 留下联系方式可以让我们更快地与您沟通</li>
                    <li>• 我们会在24小时内处理您的申诉</li>
                  </ul>
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                  提交申诉
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
